/**
 * The four firm-economics widgets (US #2239, task #2278).
 *
 * Containers are `GlassCard`; every row is a compact `Well` - these are dense
 * top-N lists, not tabular data, so they stay rows rather than becoming a
 * `Table`. Chart colours come from `useChartTokens`, the same brand tokens the
 * matter economics charts read, so they flip with the theme instead of sitting
 * on ink as a fixed indigo.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  PiBriefcaseLight,
  PiBuildingsLight,
  PiChartBarLight,
  PiUsersLight,
} from 'react-icons/pi';
import { formatCurrency } from '../../utils/formatCurrency';
import GlassCard from '../shared/brand/GlassCard';
import { Well } from '../shared/brand/Field';
import UserCell from '../Matters/economics/UserCell';
import { useChartTokens } from '../Matters/economics/chartTokens';
import type {
  FirmTopMatter,
  FirmTopClient,
  FirmUserProductivity,
} from '../../types/economics';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const money = (currency: string, v?: number | null) => formatCurrency(Number(v || 0), currency);

/** The shared shape of a list row: quiet sunken well, tight vertical rhythm. */
const ROW = 'px-3 py-2 flex items-center justify-between gap-3';

const EmptyRow: React.FC<{ label: string }> = ({ label }) => (
  <p className="text-sm text-fg-muted py-4 text-center">{label}</p>
);

/** Top matters by revenue. Clicking a row drills into its Economics tab (#2280). */
export const TopMattersWidget: React.FC<{ matters: FirmTopMatter[]; currency: string }> = ({ matters, currency }) => {
  const { t } = useTranslation();
  return (
    <GlassCard icon={<PiBriefcaseLight />} title={t('Top matters')}>
      {matters.length === 0 ? <EmptyRow label={t('No data yet.')} /> : (
        <ul className="space-y-1.5">
          {matters.map((m) => (
            <li key={m.matter_uuid}>
              <Link to={`/matters/${m.matter_uuid}?tab=Economics`} className="block group">
                <Well className={`${ROW} transition-colors group-hover:bg-surface-sunken`}>
                  <span className="text-sm text-fg-body truncate group-hover:text-fg-strong">
                    {m.matter_title || m.matter_uuid}
                  </span>
                  <span className="text-sm text-fg-strong whitespace-nowrap">
                    {money(currency, m.revenue)}
                    <span className="text-xs text-fg-muted ms-2">{Number(m.hours || 0).toFixed(1)} {t('h')}</span>
                  </span>
                </Well>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </GlassCard>
  );
};

/** Top clients by revenue. */
export const TopClientsWidget: React.FC<{ clients: FirmTopClient[]; currency: string }> = ({ clients, currency }) => {
  const { t } = useTranslation();
  return (
    <GlassCard icon={<PiBuildingsLight />} title={t('Top clients')}>
      {clients.length === 0 ? <EmptyRow label={t('No data yet.')} /> : (
        <ul className="space-y-1.5">
          {clients.map((c) => (
            <li key={c.client_id}>
              <Well className={ROW}>
                <span className="text-sm text-fg-body truncate">
                  {c.client_name || (c.client_id ? `#${c.client_id}` : t('Unassigned'))}
                </span>
                <span className="text-sm text-fg-strong whitespace-nowrap">
                  {money(currency, c.revenue)}
                  <span className="text-xs text-fg-muted ms-2">{Number(c.hours || 0).toFixed(1)} {t('h')}</span>
                </span>
              </Well>
            </li>
          ))}
        </ul>
      )}
    </GlassCard>
  );
};

/**
 * Hours/revenue by user. Clicking a row filters the dashboard to that user
 * (#2280). Utilization is the point of this widget rather than a footnote, so
 * it gets its own labelled column on the right instead of a third grey suffix.
 */
export const HoursByUserWidget: React.FC<{
  users: FirmUserProductivity[];
  currency: string;
  onUserClick?: (userId: number) => void;
}> = ({ users, currency, onUserClick }) => {
  const { t } = useTranslation();
  return (
    <GlassCard icon={<PiUsersLight />} title={t('Productivity by user')}>
      {users.length === 0 ? <EmptyRow label={t('No data yet.')} /> : (
        <ul className="space-y-1.5">
          {users.map((u) => {
            // `utilization` is a 0–1 ratio; `billable_pct` is the same idea when
            // the backend sends the split rather than the ratio.
            const util = u.utilization != null
              ? Math.round(u.utilization * 100)
              : u.billable_pct != null
                ? Math.round(u.billable_pct)
                : null;
            return (
              <li key={u.user_id}>
                <button
                  type="button"
                  onClick={() => onUserClick?.(u.user_id)}
                  className="w-full text-start group"
                >
                  <Well className={`${ROW} transition-colors group-hover:bg-surface-sunken`}>
                    <span className="min-w-0 flex-1">
                      <UserCell user={u} fallbackLabel={u.user_name || undefined} />
                    </span>
                    <span className="text-sm text-fg-strong whitespace-nowrap">
                      {Number(u.hours || 0).toFixed(1)} {t('h')}
                      <span className="text-xs text-fg-muted ms-2">{money(currency, u.revenue)}</span>
                    </span>
                    <span
                      className="w-12 text-end text-xs text-fg-muted whitespace-nowrap"
                      title={t('Utilization')}
                    >
                      {util != null ? `${util}%` : '—'}
                    </span>
                  </Well>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </GlassCard>
  );
};

/**
 * "2026-05" → "May 2026" in the active language; anything else passes through
 * unchanged. Month names come from `Intl`, never a hardcoded English array.
 */
export const formatPeriod = (period: string, locale?: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const monthIdx = Number(m[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return period;
  try {
    const label = new Intl.DateTimeFormat(locale || undefined, { month: 'short' })
      .format(new Date(Date.UTC(2000, monthIdx, 1)));
    return `${label} ${m[1]}`;
  } catch {
    return period;
  }
};

/**
 * Revenue over time. The BE returns a monthly series (revenue_trend periods like
 * "2026-05"), so this is bucketed by month; bars read better than a line for the
 * typically-sparse early data (one or two points).
 */
export const RevenueTrendWidget: React.FC<{
  trend: Array<{ period: string; revenue: number }>;
  currency: string;
}> = ({ trend, currency }) => {
  const { t, i18n } = useTranslation();
  const tokens = useChartTokens();
  const locale = i18n?.language;

  const data = useMemo(() => ({
    labels: trend.map((p) => formatPeriod(p.period, locale)),
    datasets: [{
      label: `${t('Revenue')} (${currency.toUpperCase()})`,
      data: trend.map((p) => p.revenue),
      backgroundColor: tokens.palette[0],
      hoverBackgroundColor: tokens.champagne,
      borderRadius: 6,
      maxBarThickness: 48,
    }],
  }), [trend, currency, t, tokens, locale]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx: any) => formatCurrency(Number(ctx.parsed.y || 0), currency) } },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: tokens.grid },
        ticks: { color: tokens.muted, font: { size: 10 } },
      },
      y: {
        grid: { color: tokens.grid },
        border: { display: false },
        ticks: { color: tokens.muted, font: { size: 10 }, maxTicksLimit: 5 },
      },
    },
  }), [currency, tokens]);

  return (
    <GlassCard icon={<PiChartBarLight />} title={t('Revenue trend')}>
      <div className="h-56">
        {trend.length ? <Bar data={data} options={options} /> : <EmptyRow label={t('No data yet.')} />}
      </div>
    </GlassCard>
  );
};
